/**
 * In-memory hand-off for images a use-case form has already accepted, so pressing Back
 * to change the audience doesn't wipe them.
 *
 * Why not the session draft: an uploaded image is a `File` plus a `URL.createObjectURL`
 * preview. Neither is JSON, and a `blob:` URL is only valid inside the document that
 * created it — writing one to sessionStorage restores a broken thumbnail after a
 * reload, which is worse than restoring nothing. So the images stay in memory and only
 * a plain string KEY goes into the draft. A reload empties this store, the key misses,
 * and the form honestly starts empty.
 */

export type DraftImageGroups = Record<string, unknown[]>;

const store = new Map<string, DraftImageGroups>();

let counter = 0;

/** A fresh key for one form instance. Plain string so it round-trips through the draft. */
export function newImageDraftKey(): string {
  counter += 1;
  return `img-draft-${counter}`;
}

/** Replaces, never merges — a group the caller omits is a group the user emptied. */
export function putDraftImages(key: string, groups: DraftImageGroups): void {
  store.set(key, groups);
}

export function takeDraftImages(key: string | undefined | null): DraftImageGroups | null {
  if (!key) return null;
  return store.get(key) || null;
}

/** Test hook / explicit teardown. */
export function clearDraftImages(): void {
  store.clear();
}
